import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { smart_meters_base_url } from "../../../../../../utils/urls";
import { toastify } from '../../../../../../utils/toast';
import { getItem } from '../../../../../../utils/localStorage';

const VALVE_OPERATION_DURATION = 30; // seconds

/**
 * ValveProgressBanner
 * Shown after a close/open request is submitted.
 * Counts up over VALVE_OPERATION_DURATION seconds then calls onComplete.
 */
const ValveProgressBanner = ({ actionType, onComplete }) => {
    const [elapsed, setElapsed] = useState(0);
    const intervalRef = useRef(null);

    useEffect(() => {
        intervalRef.current = setInterval(() => {
            setElapsed(prev => {
                if (prev + 1 >= VALVE_OPERATION_DURATION) {
                    clearInterval(intervalRef.current);
                    onComplete();
                    return VALVE_OPERATION_DURATION;
                }
                return prev + 1;
            });
        }, 1000);
        return () => clearInterval(intervalRef.current);
    }, []);

    const pct = Math.round((elapsed / VALVE_OPERATION_DURATION) * 100);
    const remaining = VALVE_OPERATION_DURATION - elapsed;
    const isClose = actionType === 'close';

    const barColor = isClose ? '#dc3545' : '#198754';
    const bgColor  = isClose ? '#fff5f5' : '#f0fff4';
    const borderColor = isClose ? '#f5c6cb' : '#c3e6cb';
    const textColor = isClose ? '#842029' : '#0f5132';
    const iconClass = isClose ? 'pi pi-lock' : 'pi pi-lock-open';

    return (
        <div
            style={{
                background: bgColor,
                border: `1px solid ${borderColor}`,
                borderRadius: '8px',
                padding: '14px 18px',
                marginBottom: '16px',
            }}
        >
            {/* Header row */}
            <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2">
                    <i className={iconClass} style={{ color: barColor, fontSize: '1.1rem' }} />
                    <strong style={{ color: textColor, fontSize: '0.92rem' }}>
                        {isClose ? 'Closing valve…' : 'Opening valve…'}
                    </strong>
                </div>
                <span style={{ fontSize: '0.82rem', color: textColor, fontVariantNumeric: 'tabular-nums' }}>
                    {remaining}s remaining
                </span>
            </div>

            {/* Progress bar */}
            <div
                style={{
                    height: '8px',
                    borderRadius: '4px',
                    background: '#e9ecef',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        height: '100%',
                        width: `${pct}%`,
                        background: barColor,
                        borderRadius: '4px',
                        transition: 'width 0.9s linear',
                    }}
                />
            </div>

            {/* Sub-text */}
            <p style={{ fontSize: '0.78rem', color: textColor, margin: '6px 0 0', opacity: 0.75 }}>
                Please wait. The meter is processing your request. The page will refresh automatically.
            </p>
        </div>
    );
};

const MeterInfoTab = ({ meterId }) => {
    const [meterDetails, setMeterDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [valveStatus, setValveStatus] = useState('opened');
    const [enforcementStatus, setEnforcementStatus] = useState(false);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [actionType, setActionType] = useState('');
    const [selectedReason, setSelectedReason] = useState('');
    const [actionBy, setActionBy] = useState('');
    const [modalLoading, setModalLoading] = useState(false);

    // User state
    const [fullName, setFullName] = useState('');

    // Valve operation in-progress state
    const [valveOperationActive, setValveOperationActive] = useState(false);
    const [valveOperationType, setValveOperationType] = useState('');

    const reasonOptions = {
        close: [
            { label: 'Billing Service Failed to Close', value: 'billing_service_failed_close' },
            { label: 'Caretaker Instructed', value: 'caretaker_instructed' },
            { label: 'Vacant House', value: 'vacant_house' },
            { label: 'No Longer Installed', value: 'no_longer_installed' },
            { label: 'Troubleshooting', value: 'troubleshooting' }
        ],
        open: [
            { label: 'Billing Service Failed to Open', value: 'billing_service_failed_open' },
            { label: 'Opened for Repair', value: 'opened_for_repair' },
            { label: 'Troubleshooting', value: 'troubleshooting' },
            { label: 'Caretaker Requested', value: 'caretaker_requested' },
            { label: 'Maintenance', value: 'maintenance' }
        ],
        reading: [
            { label: 'Troubleshoot', value: 'troubleshoot' },
            { label: 'Had Not Been Auto Updated for a While', value: 'not_auto_updated' },
            { label: 'To Get Realtime Readings', value: 'get_realtime_readings' }
        ]
    };

    useEffect(() => {
        const getCurrentUser = async () => {
            try {
                const currentUserValue = await getItem("APPUSER");
                let parsedUser;
                if (typeof currentUserValue === "string") {
                    try { parsedUser = JSON.parse(currentUserValue); }
                    catch (err) { console.error("Error parsing APPUSER:", err); }
                } else {
                    parsedUser = currentUserValue;
                }
                if (parsedUser?.user) {
                    setFullName(parsedUser.user.fullName);
                    setActionBy(parsedUser.user.fullName);
                }
            } catch (err) {
                console.log(err.message);
            }
        };
        getCurrentUser();
    }, []);

    useEffect(() => {
        if (meterId) fetchMeterDetails();
    }, [meterId]);

    useEffect(() => {
        const handleMeterUpdate = (event) => {
            if (event.detail.meterId === meterId) fetchMeterDetails();
        };
        window.addEventListener('meter-updated', handleMeterUpdate);
        return () => window.removeEventListener('meter-updated', handleMeterUpdate);
    }, [meterId]);

    const fetchMeterDetails = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${smart_meters_base_url}/details/${meterId}`,
                'GET',
                null
            );
            if (response.success) {
                const meterData = response.data.meter;
                setMeterDetails(meterData);
                setValveStatus(meterData?.status || 'opened');
                setEnforcementStatus(meterData?.enforcement || false);
            } else {
                toastify('Failed to fetch meter details', 'error');
            }
        } catch (error) {
            toastify('Error fetching meter details', 'error');
        } finally {
            setLoading(false);
        }
    };

    const openActionModal = (type) => {
        setActionType(type);
        setSelectedReason('');
        setActionBy(fullName);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setSelectedReason('');
        setActionBy(fullName);
        setShowModal(false);
        setModalLoading(false);
    };

    const handleModalSubmit = async () => {
        if (!selectedReason || !actionBy.trim()) {
            toastify('Please fill in all fields', 'error');
            return;
        }

        try {
            setModalLoading(true);
            let endpoint = '';
            const payload = { reason: selectedReason, actionBy: actionBy.trim() };

            if (actionType === 'reading') {
                endpoint = `${smart_meters_base_url}/get_realtime_data/${meterId}`;
            } else if (actionType === 'close') {
                endpoint = `${smart_meters_base_url}/close_meter/${meterId}`;
            } else if (actionType === 'open') {
                endpoint = `${smart_meters_base_url}/open_meter/${meterId}`;
            }

            const response = await makeRequest2(endpoint, 'POST', payload);

            if (response.success) {
                handleCloseModal();

                if (actionType === 'reading') {
                    toastify('Meter reading requested successfully', 'success');
                    setTimeout(() => fetchMeterDetails(), 3000);
                } else {
                    // Start the 30-second progress banner
                    setValveOperationType(actionType);
                    setValveOperationActive(true);

                    toastify(
                        `Valve ${actionType === 'close' ? 'close' : 'open'} command sent. Processing…`,
                        'info'
                    );

                    window.dispatchEvent(new CustomEvent('meter-updated', {
                        detail: { meterId: meterId }
                    }));
                }
            } else {
                toastify(response.message || 'Failed to process action', 'error');
            }
        } catch (error) {
            toastify('Error processing action', 'error');
        } finally {
            setModalLoading(false);
        }
    };

    // Called when the 30-second countdown finishes
    const handleValveOperationComplete = () => {
        setValveOperationActive(false);
        setValveOperationType('');
        fetchMeterDetails();
        toastify('Valve operation complete. Status refreshed.', 'success');
    };

    const handleToggleEnforcement = async () => {
        try {
            const newEnforcementStatus = !enforcementStatus;
            const response = await makeRequest2(
                `${smart_meters_base_url}/enforcement/${meterId}`,
                'PUT',
                { enforcement: newEnforcementStatus }
            );
            if (response.success) {
                setEnforcementStatus(newEnforcementStatus);
                toastify(`Enforcement ${newEnforcementStatus ? 'enabled' : 'disabled'} successfully`, 'success');
                window.dispatchEvent(new CustomEvent('meter-updated', { detail: { meterId: meterId } }));
            } else {
                toastify('Failed to update enforcement status', 'error');
            }
        } catch (error) {
            toastify('Error updating enforcement status', 'error');
        }
    };

    const getModalTitle = () => {
        switch (actionType) {
            case 'reading': return 'Get Realtime Data';
            case 'close':   return 'Close Valve';
            case 'open':    return 'Open Valve';
            default:        return 'Action';
        }
    };

    const renderActionModal = () => (
        <Dialog
            header={getModalTitle()}
            visible={showModal}
            onHide={handleCloseModal}
            style={{ width: '50vw' }}
            modal
            className="p-fluid action-modal"
        >
            <div className="container py-2">
                <div className="row mb-3">
                    <div className="col-12">
                        <label className="form-label text-sm mb-2">Reason for Action</label>
                        <select
                            className="form-select form-select-sm"
                            value={selectedReason}
                            onChange={(e) => setSelectedReason(e.target.value)}
                            disabled={modalLoading}
                        >
                            <option value="">-- Select Reason --</option>
                            {(reasonOptions[actionType] || []).map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {!selectedReason && (
                            <small className="text-muted">Please select a reason for this action</small>
                        )}
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-12">
                        <label className="form-label text-sm mb-2">Action Performed By</label>
                        <InputText
                            type="text"
                            className="form-control form-control-sm"
                            value={actionBy}
                            placeholder="Loading user..."
                            disabled={true}
                            style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                        />
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-12">
                        <div className="alert alert-info">
                            <strong>Action:</strong>{' '}
                            {actionType === 'reading'
                                ? 'This will request real-time data from the meter.'
                                : actionType === 'close'
                                ? 'This will close the meter valve, stopping water flow.'
                                : 'This will open the meter valve, allowing water flow.'}
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-4">
                    <Button
                        label="Cancel"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleCloseModal}
                        disabled={modalLoading}
                    />
                    <Button
                        label={modalLoading ? 'Processing...' : 'Confirm Action'}
                        className={`btn btn-sm ${
                            actionType === 'close' ? 'btn-danger' :
                            actionType === 'open'  ? 'btn-success' :
                            'btn-primary'
                        }`}
                        onClick={handleModalSubmit}
                        disabled={modalLoading || !selectedReason || !actionBy.trim()}
                    />
                </div>
            </div>
        </Dialog>
    );

    const statusBadgeTemplate = (status) => (
        <span className={`badge rounded-pill text-bg-${status === 'opened' ? 'success' : 'danger'}`}>
            {status}
        </span>
    );

    const installedBadgeTemplate = (isInstalled) => (
        <span className={`badge rounded-pill text-bg-${isInstalled ? 'success' : 'danger'}`}>
            {isInstalled ? 'Installed' : 'Not Installed'}
        </span>
    );

    const enforcementBadgeTemplate = (isEnforced) => (
        <span className={`badge rounded-pill text-bg-${isEnforced ? 'warning' : 'secondary'}`}>
            {isEnforced ? 'Enforced' : 'Not Enforced'}
        </span>
    );

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!meterDetails) {
        return <div className="alert alert-warning">No meter information available.</div>;
    }

    const detailsRow = {
        serialNumber:    meterDetails.meterNumber || 'N/A',
        previousReading: `${meterDetails.previousReading || '0'} m³`,
        currentReading:  `${meterDetails.currentReading  || '0'} m³`,
        lastUpdated: meterDetails.updatedAt
            ? new Date(meterDetails.updatedAt).toLocaleString()
            : 'N/A',
        unit: meterDetails.UnitInfo?.name || 'N/A'
    };

    return (
        <div className="row">
            <div className="col-sm-12">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-2">
                            {/* Disable action buttons while valve operation is in progress */}
                            <Button
                                label="Get Realtime Data"
                                className="btn btn-primary btn-sm"
                                onClick={() => openActionModal('reading')}
                                disabled={valveOperationActive}
                            />
                            {valveStatus === 'opened' ? (
                                <Button
                                    label="Close Valve"
                                    className="btn btn-danger btn-sm"
                                    onClick={() => openActionModal('close')}
                                    disabled={valveOperationActive}
                                />
                            ) : (
                                <>
                                    {!enforcementStatus && (
                                        <Button
                                            label="Open Valve"
                                            className="btn btn-success btn-sm"
                                            onClick={() => openActionModal('open')}
                                            disabled={valveOperationActive}
                                        />
                                    )}
                                    {(enforcementStatus || valveStatus === 'closed') && (
                                        <Button
                                            label={enforcementStatus ? 'Disable Enforcement' : 'Enable Enforcement'}
                                            className={`btn btn-${enforcementStatus ? 'secondary' : 'warning'} btn-sm`}
                                            onClick={handleToggleEnforcement}
                                            disabled={valveOperationActive}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    <div className="card-body">
                        {/* ---- 30-second progress banner ---- */}
                        {valveOperationActive && (
                            <ValveProgressBanner
                                actionType={valveOperationType}
                                onComplete={handleValveOperationComplete}
                            />
                        )}

                        <div className="row mb-3">
                            <div className="col-md-4 mb-3">
                                <div className="card">
                                    <div className="card-body text-center">
                                        <h6>Status</h6>
                                        {statusBadgeTemplate(valveStatus)}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4 mb-3">
                                <div className="card">
                                    <div className="card-body text-center">
                                        <h6>Installation</h6>
                                        {installedBadgeTemplate(meterDetails.isInstalled)}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4 mb-3">
                                <div className="card">
                                    <div className="card-body text-center">
                                        <h6>Enforcement</h6>
                                        {enforcementBadgeTemplate(enforcementStatus)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body">
                                        <DataTable
                                            value={[detailsRow]}
                                            emptyMessage="No meter details available."
                                            tableStyle={{ minWidth: '50rem' }}
                                        >
                                            <Column field="serialNumber"    header="Serial Number" />
                                            <Column field="previousReading" header="Previous Reading" />
                                            <Column field="currentReading"  header="Current Reading" />
                                            <Column field="lastUpdated"     header="Last Updated" />
                                            <Column field="unit"            header="Unit" />
                                        </DataTable>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {renderActionModal()}
        </div>
    );
};

export default MeterInfoTab;